import axios from 'axios';
import { ChatResponse, LocationData, ConversationListResponse } from '../types/chat';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // NOTE: Max render allows
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

export const queryAgent = async (
  query: string,
  location: LocationData | null,
  sessionId: string,
  userId?: string
): Promise<ChatResponse> => {
  const response = await api.post('/query', {
    query,
    location: location ? {
      latitude: location.latitude,
      longitude: location.longitude
    } : null,
    session_id: sessionId,
    user_id: userId,
  });
  return response.data;
};

export const getCurrentLocation = async (): Promise<LocationData> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      console.error('Geolocation API not available');
      reject(new Error('Geolocation not supported'));
      return;
    }

    console.log('Requesting location access...');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('Location retrieved successfully:', {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        console.error('Geolocation error:', {
          code: error.code,
          message: error.message,
          PERMISSION_DENIED: error.PERMISSION_DENIED,
          POSITION_UNAVAILABLE: error.POSITION_UNAVAILABLE,
          TIMEOUT: error.TIMEOUT
        });

        let errorMessage = 'Failed to get location';
        if (error.code === error.PERMISSION_DENIED) {
          errorMessage = 'Location access denied. Please allow location access in your browser settings.';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMessage = 'Location information is unavailable.';
        } else if (error.code === error.TIMEOUT) {
          errorMessage = 'Location request timed out. Please try again.';
        }

        reject(new Error(errorMessage));
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 300000,
      }
    );
  });
};

export const getConversations = async (
  userId: string,
  limit: number = 50
): Promise<ConversationListResponse> => {
  const response = await api.get(`/conversations/${userId}`, {
    params: { limit },
  });
  return response.data;
};

export const getConversationMessages = async (
  sessionId: string
): Promise<ChatResponse> => {
  const response = await api.get(`/conversation/${sessionId}/messages`);
  return response.data;
};

export default api;

